/**
 * Illustrated "How it works" section — three steps with custom SVG art:
 * phone snapping grass, magnifier over a blade, seasonal year ring.
 * Renders between the hero/upload card and the calculator chip strip.
 *
 * Client Component for scroll-into-view stagger via motion/react. SEO note:
 * this is passed as the `aboveCalculatorsSlot` into LawnCareLandingContent,
 * which suspends on useSearchParams, so on the statically prerendered `/` it
 * sits behind the Suspense fallback and its DOM is NOT in the static HTML.
 * Googlebot (and GPTBot/ClaudeBot/PerplexityBot) see the step prose only after
 * rendering JS. The crawl-safe signal is the server-rendered HowTo JSON-LD
 * emitted OUTSIDE the Suspense boundary in app/page.tsx (see the note there).
 */
"use client"

import Link from "next/link"
import { Reveal, Stagger } from "@/components/motion"
import { homepageHowToSteps } from "@/data/homepage-howto"
import {
  StepPhoneCamera,
  StepMagnifier,
  StepSeasonRing,
} from "@/components/tools/shared/how-it-works-illustrations"

// Steps come from the shared data module (data/homepage-howto.ts) so the visible
// section and the server-rendered HowTo JSON-LD in app/page.tsx stay in lockstep.
// Illustrations are matched by index. The `id="step-N"` on each Stagger.Item
// below makes the schema's `#step-N` anchor URLs resolve to real DOM targets.
const ILLUSTRATIONS = [StepPhoneCamera, StepMagnifier, StepSeasonRing]
const STEPS = homepageHowToSteps.map((step, i) => ({
  title: step.name,
  description: step.text,
  Illustration: ILLUSTRATIONS[i],
}))

export function HowItWorksIllustrated() {
  return (
    <section className="bg-background px-7 py-10 md:py-[72px]">
      <div className="max-w-[1100px] mx-auto">
        <Reveal>
          <h2 className="text-center font-bold tracking-[-0.02em] text-gray-900 mb-6 md:mb-12 text-[28px] md:text-4xl">
            How it works
          </h2>
        </Reveal>
        <Stagger className="grid grid-cols-3 md:grid-cols-3 gap-3 md:gap-8">
          {STEPS.map((step, i) => (
            <Stagger.Item key={step.title} id={`step-${i + 1}`} className="group text-center">
              <div className="relative inline-flex items-center justify-center w-14 h-14 md:w-24 md:h-24 rounded-2xl md:rounded-3xl bg-green-50 mb-3 md:mb-5 transition-transform duration-200 ease-out group-hover:scale-[1.04]">
                <div className="md:hidden">
                  <div className="scale-[0.55] origin-center">
                    <step.Illustration />
                  </div>
                </div>
                <div className="hidden md:block">
                  <step.Illustration />
                </div>
                <div className="absolute -top-1.5 -right-1.5 md:-top-2 md:-right-2 w-5 h-5 md:w-7 md:h-7 rounded-full bg-green-600 text-white text-[10px] md:text-xs font-bold flex items-center justify-center shadow-sm">
                  {i + 1}
                </div>
              </div>
              <h3 className="font-bold text-[13px] md:text-lg text-gray-900 mb-1 md:mb-1.5 leading-tight">{step.title}</h3>
              <p className="hidden md:block text-sm text-gray-500">{step.description}</p>
            </Stagger.Item>
          ))}
        </Stagger>

        {/* Hub-and-spoke internal links: establishes the homepage tool as the
            canonical hub for the grass-identification cluster and points crawlers
            at the conversational landing + the two identification guides. These
            anchors render client-side: this section is behind the useSearchParams
            Suspense fallback on `/`, so it is NOT in the static HTML (see the
            file-top note); crawlers follow them only after JS render. */}
        <p className="mt-7 md:mt-10 text-center text-sm text-gray-500">
          Rather not snap a photo? See{" "}
          <Link href="/what-kind-of-grass-do-i-have" className="text-green-700 underline underline-offset-2 hover:text-green-800">
            what kind of grass do I have
          </Link>
          ,{" "}
          <Link href="/blog/how-to-identify-your-grass-type-by-look-feel" className="text-green-700 underline underline-offset-2 hover:text-green-800">
            how to identify your grass by eye
          </Link>
          , or our{" "}
          <Link href="/blog/what-kind-of-grass-do-i-have-visual-identification-guide" className="text-green-700 underline underline-offset-2 hover:text-green-800">
            visual grass identification guide
          </Link>
          .
        </p>
      </div>
    </section>
  )
}
